import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateSponsorInput,
  UpdateSponsorInput,
  PublicSponsor,
} from "@shared/validation/sponsor.schema";
import { apiClient, ApiError } from "./client";

/** The full row, as the console sees it. */
export interface AdminSponsor extends PublicSponsor {
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const PUBLIC_KEY = ["sponsors"] as const;
const ADMIN_KEY = ["admin", "sponsors"] as const;

/** Public landing-page list — no session required. */
export function useSponsors() {
  return useQuery<PublicSponsor[], ApiError>({
    queryKey: PUBLIC_KEY,
    queryFn: () => apiClient<PublicSponsor[]>("/api/sponsors"),
    // Sponsors change a few times a year, not a few times a minute.
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdminSponsors() {
  return useQuery<AdminSponsor[], ApiError>({
    queryKey: ADMIN_KEY,
    queryFn: () => apiClient<AdminSponsor[]>("/api/admin/sponsors"),
  });
}

/** Every write refreshes BOTH caches — the console and the public page. */
function useSponsorMutation<TVars>(fn: (vars: TVars) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, TVars>({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_KEY });
      queryClient.invalidateQueries({ queryKey: PUBLIC_KEY });
    },
  });
}

export function useCreateSponsor() {
  return useSponsorMutation<CreateSponsorInput>((vars) =>
    apiClient("/api/admin/sponsors", { method: "POST", body: JSON.stringify(vars) }),
  );
}

export function useUpdateSponsor() {
  return useSponsorMutation<{ id: string; changes: UpdateSponsorInput }>(({ id, changes }) =>
    apiClient(`/api/admin/sponsors/${id}`, { method: "PUT", body: JSON.stringify(changes) }),
  );
}

export function useDeleteSponsor() {
  return useSponsorMutation<string>((id) =>
    apiClient(`/api/admin/sponsors/${id}`, { method: "DELETE" }),
  );
}
